/**
 * Quality Tabs Settings — toggle sub-tabs in the Quality page.
 * Clones the DashboardTabsTab pattern (CR-4441).
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { ShieldCheck } from 'lucide-react';
import type { QualityTabsVisibility } from '../../../lib/api/types/common';

const QUALITY_TAB_REGISTRY = [
  { id: 'ncrs', label: 'NCRs', description: 'Non-Conformance Reports' },
  { id: 'concessions', label: 'Concessions', description: 'Quality concessions and deviations' },
];

interface QualityTabsTabProps {
  qualityTabs: QualityTabsVisibility;
  saving: boolean;
  onQualityTabsChange: (tabs: QualityTabsVisibility) => void;
  onSave: () => void;
}

export function QualityTabsTab({ qualityTabs, saving, onQualityTabsChange, onSave }: QualityTabsTabProps) {
  const handleToggle = (tabId: string, enabled: boolean) => {
    onQualityTabsChange({ ...qualityTabs, [tabId]: enabled });
  };

  const enabledCount = QUALITY_TAB_REGISTRY.filter(tab => qualityTabs[tab.id] ?? true).length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Quality Tab Visibility
          </CardTitle>
          <CardDescription>
            Choose which tabs are visible on the Quality page. Disabled tabs will be hidden.
          </CardDescription>
          <span className="text-sm text-muted-foreground pt-2">
            {enabledCount} of {QUALITY_TAB_REGISTRY.length} tabs enabled
          </span>
        </CardHeader>
        <CardContent className="space-y-3">
          {QUALITY_TAB_REGISTRY.map((tab) => (
            <div key={tab.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
              <div>
                <Label className="text-base font-medium">{tab.label}</Label>
                <p className="text-sm text-muted-foreground">{tab.description}</p>
              </div>
              <Switch
                checked={qualityTabs[tab.id] ?? true}
                onCheckedChange={(checked) => handleToggle(tab.id, checked)}
              />
            </div>
          ))}
          <Button onClick={onSave} disabled={saving} className="w-full mt-4">
            {saving ? 'Saving...' : 'Save Quality Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
